import { create } from '@bufbuild/protobuf';
import {
  ContentSchema,
  Content_Kind,
  type Content,
  type Entity,
} from '@gen/chat/v1/common_pb';
import {
  CreateDirectConversationRequestSchema,
  DeleteMessageRequestSchema,
  DeleteMessageRequest_Scope,
  EditMessageRequestSchema,
  MarkReadRequestSchema,
  MethodId,
  SendMessageRequestSchema,
} from '@gen/chat/v1/methods_p1_pb';
import {
  ForwardMessageRequestSchema,
  MethodIdP2,
  SetDraftRequestSchema,
  SetPinRequestSchema,
  SetPlaybackPositionRequestSchema,
  SetReactionRequestSchema,
} from '@gen/chat/v1/methods_p2_pb';
import {
  ClosePollRequestSchema,
  CreateGroupRequestSchema,
  CreateInviteRequestSchema,
  CreatePollRequestSchema,
  DeleteTopicRequestSchema,
  JoinConversationRequestSchema,
  LeaveConversationRequestSchema,
  MethodIdP3,
  ResolveJoinRequestRequestSchema,
  RevokeInviteRequestSchema,
  SetBanRequestSchema,
  SetConversationPolicyRequestSchema,
  SetMemberRoleRequestSchema,
  TransferOwnershipRequestSchema,
  UpdateConversationRequestSchema,
  UpsertTopicRequestSchema,
  VoteRequestSchema,
} from '@gen/chat/v1/methods_p3_pb';
import {
  CreateChannelRequestSchema,
  LinkDiscussionRequestSchema,
  MethodIdP4,
  PublishPostRequestSchema,
  SubscribeChannelRequestSchema,
  UnsubscribeChannelRequestSchema,
} from '@gen/chat/v1/methods_p4_pb';
import { encodeRequest } from '@/protocol/envelope';
import { hexToBytes, type Hex } from '@/store/ids';
import type { JsonValue, NormalizedPayload, OutboxRecord, StoredContent } from '@/store/types';

/**
 * The mutations this client can queue, their canonical normalization, and the
 * encoder that turns a stored operation back into a wire request.
 *
 * Keeping all three together is the point. Section 13 defines the operation
 * identity as a hash over normalized business input, and Section 05 requires a
 * retry to carry the *original* operation identifier. If normalization and
 * encoding lived apart, a change to one would silently change the meaning of
 * operations already sitting in the outbox, and the server would reject them as
 * conflicting intent under an identifier it had already seen.
 *
 * Excluded from the canonical form, per Section 04: request_id, trace_id, the
 * connection ticket, timeouts and retry metadata. The client operation
 * identifier is excluded too — it is the lookup key the hash is compared under,
 * not part of the business input being compared.
 *
 * The three upload mutations (CreateUpload, CompleteUpload, CancelUpload) are
 * deliberately absent. Section 14 gives TransferManager its own bounded HTTP
 * work, and each of those calls needs its *response* to decide what to do next,
 * which the fire-and-reconcile outbox cannot provide. They carry stable
 * operation identifiers of their own, persisted on the upload record, so they
 * are just as idempotent under retry.
 */

export type NormalizedContent = {
  readonly kind: number;
  readonly text: string;
  readonly entities: readonly {
    readonly kind: number;
    readonly offset: number;
    readonly length: number;
    readonly value: string | null;
  }[];
  /** Attachment identities only. An expiring capability is per-attempt data. */
  readonly attachments: readonly Hex[];
  readonly reply_to: Hex | null;
  readonly topic_id: Hex | null;
};

function normalizeContent(content: StoredContent): NormalizedContent {
  return {
    kind: content.kind,
    text: content.text,
    // Entity order is the user's order, never sorted: two identical entity sets
    // applied in a different order are different intents.
    entities: content.entities.map((entity) => ({
      kind: entity.kind,
      offset: entity.offset,
      length: entity.length,
      value: entity.value,
    })),
    attachments: content.attachments.map((attachment) => attachment.mediaId.toLowerCase()),
    reply_to: content.replyTo === null ? null : content.replyTo.toLowerCase(),
    topic_id: content.topicId === null ? null : content.topicId.toLowerCase(),
  };
}

function id(value: Hex): string {
  return value.toLowerCase();
}

export function normalizeSendMessage(conversationId: Hex, content: StoredContent): NormalizedPayload {
  return {
    method: 'SendMessage',
    conversation_id: id(conversationId),
    content: normalizeContent(content),
  };
}

export function normalizeEditMessage(
  conversationId: Hex,
  messageId: Hex,
  expectedVersion: number,
  content: StoredContent,
): NormalizedPayload {
  return {
    method: 'EditMessage',
    conversation_id: id(conversationId),
    message_id: id(messageId),
    expected_version: expectedVersion,
    content: normalizeContent(content),
  };
}

export function normalizeDeleteMessage(
  conversationId: Hex,
  messageId: Hex,
  scope: DeleteMessageRequest_Scope,
  expectedVersion: number,
): NormalizedPayload {
  return {
    method: 'DeleteMessage',
    conversation_id: id(conversationId),
    message_id: id(messageId),
    scope,
    expected_version: expectedVersion,
  };
}

export function normalizeCreateDirectConversation(peerId: Hex): NormalizedPayload {
  return {
    method: 'CreateDirectConversation',
    peer_id: id(peerId),
  };
}

export function normalizeMarkRead(conversationId: Hex, lastReadCreatedSeq: number): NormalizedPayload {
  return {
    method: 'MarkRead',
    conversation_id: id(conversationId),
    last_read_created_seq: lastReadCreatedSeq,
  };
}

// ---------------------------------------------------------------------------
// Phase 2
// ---------------------------------------------------------------------------

export function normalizeSetReaction(
  conversationId: Hex,
  messageId: Hex,
  reaction: string,
  active: boolean,
): NormalizedPayload {
  return {
    method: 'SetReaction',
    conversation_id: id(conversationId),
    message_id: id(messageId),
    reaction,
    active,
  };
}

export function normalizeSetDraft(
  conversationId: Hex,
  expectedVersion: number,
  content: StoredContent,
  pendingUploadIds: readonly Hex[],
): NormalizedPayload {
  return {
    method: 'SetDraft',
    conversation_id: id(conversationId),
    expected_version: expectedVersion,
    content: normalizeContent(content),
    // Section 15: "repeat order follows the draft's attachment order", so this
    // list is never sorted.
    pending_upload_ids: pendingUploadIds.map(id),
  };
}

export function normalizeSetPin(
  conversationId: Hex,
  messageId: Hex,
  pinned: boolean,
): NormalizedPayload {
  return {
    method: 'SetPin',
    conversation_id: id(conversationId),
    message_id: id(messageId),
    pinned,
  };
}

export function normalizeForwardMessage(
  sourceConversationId: Hex,
  messageId: Hex,
  destinationConversationId: Hex,
): NormalizedPayload {
  return {
    method: 'ForwardMessage',
    source_conversation_id: id(sourceConversationId),
    message_id: id(messageId),
    destination_conversation_id: id(destinationConversationId),
  };
}

export function normalizeSetPlaybackPosition(
  conversationId: Hex,
  messageId: Hex,
  positionMs: number,
): NormalizedPayload {
  return {
    method: 'SetPlaybackPosition',
    conversation_id: id(conversationId),
    message_id: id(messageId),
    position_ms: positionMs,
  };
}

// ---------------------------------------------------------------------------
// Phase 3
// ---------------------------------------------------------------------------

export function normalizeCreateGroup(
  title: string,
  initialMembers: readonly Hex[],
  historyPolicy: string,
): NormalizedPayload {
  return {
    method: 'CreateGroup',
    title,
    initial_members: initialMembers.map(id),
    history_policy: historyPolicy,
  };
}

export function normalizeCreateInvite(
  conversationId: Hex,
  maxUses: number,
  expiresAt: number,
  approval: boolean,
): NormalizedPayload {
  return {
    method: 'CreateInvite',
    conversation_id: id(conversationId),
    max_uses: maxUses,
    expires_at: expiresAt,
    approval,
  };
}

export function normalizeRevokeInvite(conversationId: Hex, tokenHash: Hex): NormalizedPayload {
  return { method: 'RevokeInvite', conversation_id: id(conversationId), token_hash: id(tokenHash) };
}

export function normalizeJoinConversation(
  token: string | null,
  publicUsername: string | null,
): NormalizedPayload {
  return { method: 'JoinConversation', token, public_username: publicUsername };
}

export function normalizeLeaveConversation(conversationId: Hex): NormalizedPayload {
  return { method: 'LeaveConversation', conversation_id: id(conversationId) };
}

export function normalizeSetMemberRole(
  conversationId: Hex,
  userId: Hex,
  role: string,
  permissions: number,
  expectedPolicyVersion: number,
): NormalizedPayload {
  return {
    method: 'SetMemberRole',
    conversation_id: id(conversationId),
    user_id: id(userId),
    role,
    permissions,
    expected_policy_version: expectedPolicyVersion,
  };
}

export function normalizeSetBan(
  conversationId: Hex,
  userId: Hex,
  banned: boolean,
): NormalizedPayload {
  return {
    method: 'SetBan',
    conversation_id: id(conversationId),
    user_id: id(userId),
    banned,
  };
}

export function normalizeTransferOwnership(
  conversationId: Hex,
  targetUserId: Hex,
): NormalizedPayload {
  // The recent-authentication ticket is deliberately excluded from the hash: it
  // is a per-attempt credential, like the connection ticket, and including it
  // would make a retry look like a different intent.
  return {
    method: 'TransferOwnership',
    conversation_id: id(conversationId),
    target_user_id: id(targetUserId),
  };
}

export function normalizeUpsertTopic(
  conversationId: Hex,
  topicId: Hex | null,
  title: string,
  closed: boolean,
  expectedVersion: number,
): NormalizedPayload {
  return {
    method: 'UpsertTopic',
    conversation_id: id(conversationId),
    topic_id: topicId === null ? null : id(topicId),
    title,
    closed,
    expected_version: expectedVersion,
  };
}

export function normalizeDeleteTopic(
  conversationId: Hex,
  topicId: Hex,
  expectedVersion: number,
): NormalizedPayload {
  return {
    method: 'DeleteTopic',
    conversation_id: id(conversationId),
    topic_id: id(topicId),
    expected_version: expectedVersion,
  };
}

export function normalizeCreatePoll(input: {
  conversationId: Hex;
  question: string;
  options: readonly string[];
  multiple: boolean;
  anonymous: boolean;
  correctIndex: number | null;
  explanation: string | null;
  closesAt: number | null;
}): NormalizedPayload {
  return {
    method: 'CreatePoll',
    conversation_id: id(input.conversationId),
    question: input.question,
    options: [...input.options],
    multiple: input.multiple,
    anonymous: input.anonymous,
    correct_index: input.correctIndex,
    explanation: input.explanation,
    closes_at: input.closesAt,
  };
}

export function normalizeVote(
  conversationId: Hex,
  pollId: Hex,
  optionIds: readonly Hex[],
): NormalizedPayload {
  return {
    method: 'Vote',
    conversation_id: id(conversationId),
    poll_id: id(pollId),
    // Not sorted: a multiple-choice vote is the set the user chose, and the
    // server compares the same list it was sent.
    option_ids: optionIds.map(id),
  };
}

export function normalizeClosePoll(
  conversationId: Hex,
  pollId: Hex,
  expectedVersion: number,
): NormalizedPayload {
  return {
    method: 'ClosePoll',
    conversation_id: id(conversationId),
    poll_id: id(pollId),
    expected_version: expectedVersion,
  };
}

export function normalizeResolveJoinRequest(
  conversationId: Hex,
  requesterId: Hex,
  approve: boolean,
): NormalizedPayload {
  return {
    method: 'ResolveJoinRequest',
    conversation_id: id(conversationId),
    requester_id: id(requesterId),
    approve,
  };
}

export function normalizeSetConversationPolicy(input: {
  conversationId: Hex;
  expectedVersion: number;
  slowModeSeconds: number;
  protectedContent: boolean;
  historyPolicy: string;
}): NormalizedPayload {
  return {
    method: 'SetConversationPolicy',
    conversation_id: id(input.conversationId),
    expected_version: input.expectedVersion,
    slow_mode_seconds: input.slowModeSeconds,
    protected_content: input.protectedContent,
    history_policy: input.historyPolicy,
  };
}

export function normalizeUpdateConversation(
  conversationId: Hex,
  title: string,
  publicUsername: string | null,
  expectedVersion: number,
): NormalizedPayload {
  return {
    method: 'UpdateConversation',
    conversation_id: id(conversationId),
    title,
    public_username: publicUsername,
    expected_version: expectedVersion,
  };
}

// ---------------------------------------------------------------------------
// Phase 4
// ---------------------------------------------------------------------------

export function normalizeCreateChannel(
  title: string,
  publicUsername: string | null,
): NormalizedPayload {
  return { method: 'CreateChannel', title, public_username: publicUsername };
}

export function normalizeSubscribeChannel(
  conversationId: Hex,
  inviteToken: string | null,
): NormalizedPayload {
  return {
    method: 'SubscribeChannel',
    conversation_id: id(conversationId),
    invite_token: inviteToken,
  };
}

export function normalizeUnsubscribeChannel(conversationId: Hex): NormalizedPayload {
  return { method: 'UnsubscribeChannel', conversation_id: id(conversationId) };
}

export function normalizePublishPost(
  conversationId: Hex,
  content: StoredContent,
  silent: boolean,
): NormalizedPayload {
  return {
    method: 'PublishPost',
    conversation_id: id(conversationId),
    content: normalizeContent(content),
    silent,
  };
}

export function normalizeLinkDiscussion(channelId: Hex, groupId: Hex | null): NormalizedPayload {
  return {
    method: 'LinkDiscussion',
    channel_id: id(channelId),
    group_id: groupId === null ? null : id(groupId),
  };
}

// ---------------------------------------------------------------------------
// Reading a stored operation back
// ---------------------------------------------------------------------------

/**
 * Narrows a stored JSON value to an object.
 *
 * `Array.isArray` alone does not remove `readonly JsonValue[]` from the union,
 * so the check is wrapped in a predicate rather than repeated at each call
 * site with a cast to make the compiler agree.
 */
function isJsonObject(value: JsonValue | undefined): value is { readonly [key: string]: JsonValue } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(payload: NormalizedPayload, key: string): string {
  const value = payload[key];
  if (typeof value !== 'string') {
    throw new Error(`stored operation is missing the string field "${key}"`);
  }
  return value;
}

function readOptionalString(payload: NormalizedPayload, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' ? value : null;
}

function readNumber(payload: NormalizedPayload, key: string): number {
  const value = payload[key];
  if (typeof value !== 'number') {
    throw new Error(`stored operation is missing the numeric field "${key}"`);
  }
  return value;
}

function readBoolean(payload: NormalizedPayload, key: string): boolean {
  const value = payload[key];
  if (typeof value !== 'boolean') {
    throw new Error(`stored operation is missing the boolean field "${key}"`);
  }
  return value;
}

function readStringList(payload: NormalizedPayload, key: string): string[] {
  const value = payload[key];
  if (!Array.isArray(value)) {
    throw new Error(`stored operation is missing the list field "${key}"`);
  }
  return value.map((item) => {
    if (typeof item !== 'string') {
      throw new Error(`stored operation has a non-string entry in "${key}"`);
    }
    return item;
  });
}

function readIdList(payload: NormalizedPayload, key: string): Uint8Array[] {
  return readStringList(payload, key).map(hexToBytes);
}

/**
 * Rebuilds wire content from a stored normalized payload.
 *
 * The shape is validated rather than asserted. The record may have been written
 * by an earlier build of this client and then sat in the outbox across an
 * upgrade, so a field that has changed shape has to fail loudly here — where
 * the scheduler turns it into a visible terminal failure the user can retry or
 * discard — instead of being cast into a malformed request the server rejects
 * as an operation conflict under an identifier that can never be reused.
 *
 * Attachment metadata comes from the outbox record rather than the normalized
 * payload. Only the media identifiers belong in the hashed intent: the wire
 * message also carries size, MIME, dimensions and duration, which are facts
 * about the object rather than choices the user made, and a capability token,
 * which Section 06 binds to a short-lived grant and which would therefore be
 * stale by the time a queued operation is retried.
 */
function readContent(
  payload: NormalizedPayload,
  attachments: readonly StoredContentAttachment[],
): Content {
  const record = payload['content'];
  if (!isJsonObject(record)) {
    throw new Error('stored operation is missing its content');
  }

  const kind = record['kind'];
  const text = record['text'];
  const replyTo = record['reply_to'];
  const topicId = record['topic_id'];
  const rawEntities = record['entities'];
  if (typeof kind !== 'number' || typeof text !== 'string' || !Array.isArray(rawEntities)) {
    throw new Error('stored operation has content in an unrecognized shape');
  }
  if (replyTo !== null && typeof replyTo !== 'string' && replyTo !== undefined) {
    throw new Error('stored operation has an unrecognized reply target');
  }

  const entities: Entity[] = rawEntities.map((entity) => {
    if (!isJsonObject(entity)) {
      throw new Error('stored operation has an unrecognized entity');
    }
    const entityKind = entity['kind'];
    const offset = entity['offset'];
    const length = entity['length'];
    const value = entity['value'];
    if (typeof entityKind !== 'number' || typeof offset !== 'number' || typeof length !== 'number') {
      throw new Error('stored operation has an unrecognized entity');
    }
    return {
      $typeName: 'chat.v1.Entity' as const,
      kind: entityKind,
      offset,
      length,
      ...(typeof value === 'string' ? { value } : {}),
    };
  });

  return create(ContentSchema, {
    kind: kind as Content_Kind,
    text,
    entities,
    attachments: attachments.map((attachment) => ({
      mediaId: hexToBytes(attachment.mediaId),
      sizeBytes: BigInt(attachment.sizeBytes),
      mime: attachment.mime,
      ...(attachment.caption === null ? {} : { caption: attachment.caption }),
      ...(attachment.filename === null ? {} : { filename: attachment.filename }),
      ...(attachment.width === null ? {} : { width: attachment.width }),
      ...(attachment.height === null ? {} : { height: attachment.height }),
      ...(attachment.durationMs === null ? {} : { durationMs: attachment.durationMs }),
    })),
    ...(typeof replyTo === 'string' ? { replyTo: hexToBytes(replyTo) } : {}),
    ...(typeof topicId === 'string' ? { topicId: hexToBytes(topicId) } : {}),
  });
}

/** The attachment fields an encoder needs; a structural subset of StoredAttachment. */
export interface StoredContentAttachment {
  mediaId: Hex;
  caption: string | null;
  filename: string | null;
  mime: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
}

/**
 * Re-encodes a stored operation for one network attempt.
 *
 * `requestId` changes on every attempt; the operation identifier inside the
 * body never does.
 */
export function encodeOutboxRequest(record: OutboxRecord, requestId: Uint8Array): Uint8Array {
  const payload = record.normalizedPayload;
  const clientOpId = hexToBytes(record.opId);
  const attachments = record.attachments;

  switch (record.methodId) {
    case MethodId.SEND_MESSAGE:
      return encodeRequest(MethodId.SEND_MESSAGE, requestId, SendMessageRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        content: readContent(payload, attachments),
        clientOpId,
      });

    case MethodId.EDIT_MESSAGE:
      return encodeRequest(MethodId.EDIT_MESSAGE, requestId, EditMessageRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        messageId: hexToBytes(readString(payload, 'message_id')),
        expectedVersion: BigInt(readNumber(payload, 'expected_version')),
        content: readContent(payload, attachments),
        clientOpId,
      });

    case MethodId.DELETE_MESSAGE:
      return encodeRequest(MethodId.DELETE_MESSAGE, requestId, DeleteMessageRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        messageId: hexToBytes(readString(payload, 'message_id')),
        scope: readNumber(payload, 'scope') as DeleteMessageRequest_Scope,
        expectedVersion: BigInt(readNumber(payload, 'expected_version')),
        clientOpId,
      });

    case MethodId.CREATE_DIRECT_CONVERSATION:
      return encodeRequest(
        MethodId.CREATE_DIRECT_CONVERSATION,
        requestId,
        CreateDirectConversationRequestSchema,
        {
          peerId: hexToBytes(readString(payload, 'peer_id')),
          clientOpId,
        },
      );

    case MethodId.MARK_READ:
      return encodeRequest(MethodId.MARK_READ, requestId, MarkReadRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        lastReadCreatedSeq: BigInt(readNumber(payload, 'last_read_created_seq')),
        clientOpId,
      });

    // --- Phase 2 ---------------------------------------------------------

    case MethodIdP2.METHOD_ID_SET_REACTION:
      return encodeRequest(MethodIdP2.METHOD_ID_SET_REACTION, requestId, SetReactionRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        messageId: hexToBytes(readString(payload, 'message_id')),
        reaction: readString(payload, 'reaction'),
        active: readBoolean(payload, 'active'),
        clientOpId,
      });

    case MethodIdP2.METHOD_ID_SET_DRAFT:
      return encodeRequest(MethodIdP2.METHOD_ID_SET_DRAFT, requestId, SetDraftRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        expectedVersion: BigInt(readNumber(payload, 'expected_version')),
        content: readContent(payload, attachments),
        pendingUploadIds: readIdList(payload, 'pending_upload_ids'),
        clientOpId,
      });

    case MethodIdP2.METHOD_ID_SET_PIN:
      return encodeRequest(MethodIdP2.METHOD_ID_SET_PIN, requestId, SetPinRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        messageId: hexToBytes(readString(payload, 'message_id')),
        pinned: readBoolean(payload, 'pinned'),
        clientOpId,
      });

    case MethodIdP2.METHOD_ID_FORWARD_MESSAGE:
      return encodeRequest(
        MethodIdP2.METHOD_ID_FORWARD_MESSAGE,
        requestId,
        ForwardMessageRequestSchema,
        {
          sourceConversationId: hexToBytes(readString(payload, 'source_conversation_id')),
          messageId: hexToBytes(readString(payload, 'message_id')),
          destinationConversationId: hexToBytes(readString(payload, 'destination_conversation_id')),
          clientOpId,
        },
      );

    case MethodIdP2.METHOD_ID_SET_PLAYBACK_POSITION:
      return encodeRequest(
        MethodIdP2.METHOD_ID_SET_PLAYBACK_POSITION,
        requestId,
        SetPlaybackPositionRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          messageId: hexToBytes(readString(payload, 'message_id')),
          positionMs: readNumber(payload, 'position_ms'),
          clientOpId,
        },
      );

    // --- Phase 3 ---------------------------------------------------------

    case MethodIdP3.METHOD_ID_CREATE_GROUP:
      return encodeRequest(MethodIdP3.METHOD_ID_CREATE_GROUP, requestId, CreateGroupRequestSchema, {
        title: readString(payload, 'title'),
        initialMembers: readIdList(payload, 'initial_members'),
        historyPolicy: readString(payload, 'history_policy'),
        clientOpId,
      });

    case MethodIdP3.METHOD_ID_CREATE_INVITE:
      return encodeRequest(
        MethodIdP3.METHOD_ID_CREATE_INVITE,
        requestId,
        CreateInviteRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          maxUses: readNumber(payload, 'max_uses'),
          expiresAt: BigInt(readNumber(payload, 'expires_at')),
          approval: readBoolean(payload, 'approval'),
          clientOpId,
        },
      );

    case MethodIdP3.METHOD_ID_REVOKE_INVITE:
      return encodeRequest(
        MethodIdP3.METHOD_ID_REVOKE_INVITE,
        requestId,
        RevokeInviteRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          tokenHash: hexToBytes(readString(payload, 'token_hash')),
          clientOpId,
        },
      );

    case MethodIdP3.METHOD_ID_JOIN_CONVERSATION: {
      const token = readOptionalString(payload, 'token');
      const username = readOptionalString(payload, 'public_username');
      return encodeRequest(
        MethodIdP3.METHOD_ID_JOIN_CONVERSATION,
        requestId,
        JoinConversationRequestSchema,
        {
          ...(token === null ? {} : { token }),
          ...(username === null ? {} : { publicUsername: username }),
          clientOpId,
        },
      );
    }

    case MethodIdP3.METHOD_ID_LEAVE_CONVERSATION:
      return encodeRequest(
        MethodIdP3.METHOD_ID_LEAVE_CONVERSATION,
        requestId,
        LeaveConversationRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          clientOpId,
        },
      );

    case MethodIdP3.METHOD_ID_SET_MEMBER_ROLE:
      return encodeRequest(
        MethodIdP3.METHOD_ID_SET_MEMBER_ROLE,
        requestId,
        SetMemberRoleRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          userId: hexToBytes(readString(payload, 'user_id')),
          role: readString(payload, 'role'),
          permissions: BigInt(readNumber(payload, 'permissions')),
          expectedPolicyVersion: BigInt(readNumber(payload, 'expected_policy_version')),
          clientOpId,
        },
      );

    case MethodIdP3.METHOD_ID_SET_BAN:
      return encodeRequest(MethodIdP3.METHOD_ID_SET_BAN, requestId, SetBanRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        userId: hexToBytes(readString(payload, 'user_id')),
        banned: readBoolean(payload, 'banned'),
        clientOpId,
      });

    case MethodIdP3.METHOD_ID_TRANSFER_OWNERSHIP:
      return encodeRequest(
        MethodIdP3.METHOD_ID_TRANSFER_OWNERSHIP,
        requestId,
        TransferOwnershipRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          targetUserId: hexToBytes(readString(payload, 'target_user_id')),
          // Section 06 requires recent authentication for an ownership
          // transfer. The ticket is a per-attempt credential that the outbox
          // cannot carry across a retry, so this method is queued only from a
          // freshly authenticated action and the field is filled by the
          // scheduler's ticket hook. Until that hook exists (P3-04) the
          // transfer is offered only while a ticket is in hand.
          recentAuthTicket: readString(payload, 'recent_auth_ticket'),
          clientOpId,
        },
      );

    case MethodIdP3.METHOD_ID_UPSERT_TOPIC: {
      const topicId = readOptionalString(payload, 'topic_id');
      return encodeRequest(MethodIdP3.METHOD_ID_UPSERT_TOPIC, requestId, UpsertTopicRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        ...(topicId === null ? {} : { topicId: hexToBytes(topicId) }),
        title: readString(payload, 'title'),
        closed: readBoolean(payload, 'closed'),
        expectedVersion: BigInt(readNumber(payload, 'expected_version')),
        clientOpId,
      });
    }

    case MethodIdP3.METHOD_ID_DELETE_TOPIC:
      return encodeRequest(MethodIdP3.METHOD_ID_DELETE_TOPIC, requestId, DeleteTopicRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        topicId: hexToBytes(readString(payload, 'topic_id')),
        expectedVersion: BigInt(readNumber(payload, 'expected_version')),
        clientOpId,
      });

    case MethodIdP3.METHOD_ID_CREATE_POLL: {
      const correctIndex = payload['correct_index'];
      const explanation = readOptionalString(payload, 'explanation');
      const closesAt = payload['closes_at'];
      return encodeRequest(MethodIdP3.METHOD_ID_CREATE_POLL, requestId, CreatePollRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        question: readString(payload, 'question'),
        options: readStringList(payload, 'options'),
        multiple: readBoolean(payload, 'multiple'),
        anonymous: readBoolean(payload, 'anonymous'),
        ...(typeof correctIndex === 'number' ? { correctIndex } : {}),
        ...(explanation === null ? {} : { explanation }),
        ...(typeof closesAt === 'number' ? { closesAt: BigInt(closesAt) } : {}),
        clientOpId,
      });
    }

    case MethodIdP3.METHOD_ID_VOTE:
      return encodeRequest(MethodIdP3.METHOD_ID_VOTE, requestId, VoteRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        pollId: hexToBytes(readString(payload, 'poll_id')),
        optionIds: readIdList(payload, 'option_ids'),
        clientOpId,
      });

    case MethodIdP3.METHOD_ID_CLOSE_POLL:
      return encodeRequest(MethodIdP3.METHOD_ID_CLOSE_POLL, requestId, ClosePollRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        pollId: hexToBytes(readString(payload, 'poll_id')),
        expectedVersion: BigInt(readNumber(payload, 'expected_version')),
        clientOpId,
      });

    case MethodIdP3.METHOD_ID_RESOLVE_JOIN_REQUEST:
      return encodeRequest(
        MethodIdP3.METHOD_ID_RESOLVE_JOIN_REQUEST,
        requestId,
        ResolveJoinRequestRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          requesterId: hexToBytes(readString(payload, 'requester_id')),
          approve: readBoolean(payload, 'approve'),
          clientOpId,
        },
      );

    case MethodIdP3.METHOD_ID_SET_CONVERSATION_POLICY:
      return encodeRequest(
        MethodIdP3.METHOD_ID_SET_CONVERSATION_POLICY,
        requestId,
        SetConversationPolicyRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          expectedVersion: BigInt(readNumber(payload, 'expected_version')),
          slowModeSeconds: readNumber(payload, 'slow_mode_seconds'),
          protectedContent: readBoolean(payload, 'protected_content'),
          historyPolicy: readString(payload, 'history_policy'),
          clientOpId,
        },
      );

    case MethodIdP3.METHOD_ID_UPDATE_CONVERSATION: {
      const publicUsername = readOptionalString(payload, 'public_username');
      return encodeRequest(
        MethodIdP3.METHOD_ID_UPDATE_CONVERSATION,
        requestId,
        UpdateConversationRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          title: readString(payload, 'title'),
          ...(publicUsername === null ? {} : { publicUsername }),
          expectedVersion: BigInt(readNumber(payload, 'expected_version')),
          clientOpId,
        },
      );
    }

    // --- Phase 4 ---------------------------------------------------------

    case MethodIdP4.METHOD_ID_CREATE_CHANNEL: {
      const publicUsername = readOptionalString(payload, 'public_username');
      return encodeRequest(
        MethodIdP4.METHOD_ID_CREATE_CHANNEL,
        requestId,
        CreateChannelRequestSchema,
        {
          title: readString(payload, 'title'),
          ...(publicUsername === null ? {} : { publicUsername }),
          clientOpId,
        },
      );
    }

    case MethodIdP4.METHOD_ID_SUBSCRIBE_CHANNEL: {
      const inviteToken = readOptionalString(payload, 'invite_token');
      return encodeRequest(
        MethodIdP4.METHOD_ID_SUBSCRIBE_CHANNEL,
        requestId,
        SubscribeChannelRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          ...(inviteToken === null ? {} : { inviteToken }),
          clientOpId,
        },
      );
    }

    case MethodIdP4.METHOD_ID_UNSUBSCRIBE_CHANNEL:
      return encodeRequest(
        MethodIdP4.METHOD_ID_UNSUBSCRIBE_CHANNEL,
        requestId,
        UnsubscribeChannelRequestSchema,
        {
          conversationId: hexToBytes(readString(payload, 'conversation_id')),
          clientOpId,
        },
      );

    case MethodIdP4.METHOD_ID_PUBLISH_POST:
      return encodeRequest(MethodIdP4.METHOD_ID_PUBLISH_POST, requestId, PublishPostRequestSchema, {
        conversationId: hexToBytes(readString(payload, 'conversation_id')),
        content: readContent(payload, attachments),
        silent: readBoolean(payload, 'silent'),
        clientOpId,
      });

    case MethodIdP4.METHOD_ID_LINK_DISCUSSION: {
      const groupId = readOptionalString(payload, 'group_id');
      return encodeRequest(
        MethodIdP4.METHOD_ID_LINK_DISCUSSION,
        requestId,
        LinkDiscussionRequestSchema,
        {
          channelId: hexToBytes(readString(payload, 'channel_id')),
          ...(groupId === null ? {} : { groupId: hexToBytes(groupId) }),
          clientOpId,
        },
      );
    }

    default:
      throw new Error(`no encoder for method ${String(record.methodId)}`);
  }
}

/** Content for a plain text message. */
export function textContent(text: string, replyTo: Hex | null, topicId: Hex | null = null): StoredContent {
  return {
    kind: Content_Kind.TEXT,
    text,
    entities: [],
    attachments: [],
    replyTo,
    topicId,
    embeddedId: null,
  };
}

/**
 * Content kind for a set of attachments.
 *
 * Section 06 makes an album a single message with 2–10 ordered attachment
 * references, so more than one attachment is an ALBUM regardless of what the
 * individual objects are. A single attachment takes the kind of its own media,
 * which is what decides whether the receiving client offers playback or a
 * download.
 */
export function attachmentContentKind(
  mediaKinds: readonly string[],
): Content_Kind {
  if (mediaKinds.length > 1) {
    return Content_Kind.ALBUM;
  }
  switch (mediaKinds[0]) {
    case 'photo':
      return Content_Kind.PHOTO;
    case 'video':
      return Content_Kind.VIDEO;
    case 'voice':
      return Content_Kind.VOICE;
    case 'video_note':
      return Content_Kind.VIDEO_NOTE;
    default:
      return Content_Kind.FILE;
  }
}
